import type { TyreLayout, TyreLayoutInput, TyreLayoutSubjectKind } from '@chukua/common';
import { Alert, Button, Form } from 'antd';
import { useState } from 'react';
import { useParams } from 'react-router';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { withinSection, type Crumb } from '../../../../../shared/components/section-trail';
import { REFERENCE_DATA_TAB_LABELS, referenceDataTabPath } from '../../../reference-data.paths';
import { AxleArrangementEditor } from '../components/AxleArrangementEditor';
import { TyreLayoutCanvas } from '../components/TyreLayoutCanvas';
import { VehicleClassDetails } from '../components/VehicleClassDetails';
import {
  SUBJECT_NOUN,
  VEHICLE_CLASS_GRID,
  VehicleClassLoadError,
  VehicleClassMissing,
  VehicleClassPageSkeleton,
} from '../components/VehicleClassPageFallbacks';
import { useVehicleClass, type VehicleClass, type VehicleClassInput } from '../hooks/use-vehicle-class';
import { useVehicleClassEditor } from '../hooks/use-vehicle-class-editor';
import { bodyStyleFor, bodyStyleLabel } from '@chukua/vehicle-model';
import { toVehicleClassInput } from '../policies/vehicle-class-form.policy';
import type { TyreDiagramAxle, TyreDiagramView } from '../public/TyrePositionDiagram';
import { NEW_VEHICLE_CLASS, VEHICLE_CLASS_TABS } from '../tyre-layouts.paths';

function describe(parts: { code: string; bodyStyle: string; axles: TyreDiagramAxle[]; isChanged: boolean }): string {
  const positions = parts.axles.reduce((sum, axle) => sum + axle.positions.length, 0);
  const axles = `${parts.axles.length} ${parts.axles.length === 1 ? 'axle' : 'axles'}`;
  const summary = [parts.code, bodyStyleLabel(parts.bodyStyle), axles, `${positions} ${positions === 1 ? 'position' : 'positions'}`].filter(Boolean);
  return [...summary, ...(parts.isChanged ? ['unsaved changes'] : [])].join(' · ');
}

interface EditorActionsProps {
  kind: TyreLayoutSubjectKind;
  isExisting: boolean;
  isChanged: boolean;
  saving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}

function EditorActions({ kind, isExisting, isChanged, saving, onDiscard, onSave }: EditorActionsProps) {
  return (
    <>
      {isExisting && (
        <Button type="text" disabled={!isChanged || saving} onClick={onDiscard}>
          Discard changes
        </Button>
      )}
      <Button type="primary" loading={saving} disabled={!isChanged} onClick={onSave}>
        {isExisting ? 'Save' : `Create ${SUBJECT_NOUN[kind]}`}
      </Button>
    </>
  );
}

interface EditorProps {
  kind: TyreLayoutSubjectKind;
  trail: Crumb[];
  subject: VehicleClass | null;
  layout: TyreLayout | null;
  saving: boolean;
  saveError: string | null;
  onSave: (details: VehicleClassInput, layout: TyreLayoutInput | null) => Promise<boolean>;
}

function VehicleClassEditor({ kind, trail, subject, layout, saving, saveError, onSave }: EditorProps) {
  const editor = useVehicleClassEditor(kind, subject, layout);
  const [view, setView] = useState<TyreDiagramView>('3d');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const toggleSelected = (code: string) => setSelectedCode((current) => (current === code ? null : code));
  const bodyStyle = bodyStyleFor(kind, editor.bodyStyle);
  const description = describe({ code: subject?.code ?? '', bodyStyle, axles: editor.shownAxles, isChanged: subject !== null && editor.isChanged });

  return (
    <>
      <PageHeader
        title={subject?.name ?? `New ${SUBJECT_NOUN[kind]}`}
        breadcrumbs={trail}
        description={description}
        actions={
          <EditorActions
            kind={kind}
            isExisting={subject !== null}
            isChanged={editor.isChanged}
            saving={saving}
            onDiscard={editor.discard}
            onSave={() => editor.form.submit()}
          />
        }
      />
      <Form
        form={editor.form}
        layout="vertical"
        requiredMark={false}
        disabled={saving}
        onFinish={(values) => onSave(toVehicleClassInput(kind, values) as VehicleClassInput, editor.layoutInput)}
      >
        {saveError && <Alert className="mb-4" type="error" title={saveError} showIcon />}
        <div className={VEHICLE_CLASS_GRID}>
          <TyreLayoutCanvas
            subjectKind={kind}
            bodyStyle={bodyStyle}
            axles={editor.shownAxles}
            view={view}
            onViewChange={setView}
            selectedCode={selectedCode}
            onSelect={toggleSelected}
          />
          <div className="flex min-w-0 flex-col gap-4">
            <VehicleClassDetails kind={kind} isNew={subject === null} />
            <AxleArrangementEditor
              kind={kind}
              axles={editor.axles}
              onChange={editor.setAxles}
              positions={editor.shownAxles}
              retiredCodes={editor.retired}
              selectedCode={selectedCode}
              onSelect={toggleSelected}
            />
          </div>
        </div>
      </Form>
    </>
  );
}

/**
 * A truck class's or trailer type's own page (gitaumoses4/chukua#176
 * follow-up), new or existing: the vehicle in 3D beside its details and its
 * axles, all saved together. It replaced the catalogue's edit drawer and the
 * separate tyre layout page, whose address now redirects here.
 */
export function VehicleClassPage({ subjectKind }: { subjectKind: TyreLayoutSubjectKind }) {
  const { code: param = '' } = useParams();
  const code = param === NEW_VEHICLE_CLASS ? null : param;
  const tab = VEHICLE_CLASS_TABS[subjectKind];
  const trail = withinSection('reference-data', { label: REFERENCE_DATA_TAB_LABELS[tab], to: referenceDataTabPath(tab) });
  const { state, saving, saveError, save } = useVehicleClass(subjectKind, code);

  if (state.status === 'loading') return <VehicleClassPageSkeleton trail={trail} kind={subjectKind} />;
  if (state.status === 'missing') return <VehicleClassMissing trail={trail} kind={subjectKind} code={param} />;
  if (state.status === 'error') return <VehicleClassLoadError trail={trail} kind={subjectKind} />;
  return (
    <VehicleClassEditor kind={subjectKind} trail={trail} subject={state.subject} layout={state.layout} saving={saving} saveError={saveError} onSave={save} />
  );
}
